/// <reference path="../types/global.d.ts" />
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Plus, Share2 } from 'lucide-react';
import { NavigationProps } from "@/types/navigation";
import { Transaction } from "@/types/transaction";
import { Map } from '../components/map/Map';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { mockTransactions } from '@/lib/mockData';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from "../components/ui/breadcrumb";
import { SidebarTrigger } from "../components/ui/sidebar";
import { Separator } from "../components/ui/separator";

interface PlacesPageProps extends NavigationProps {}

const badgeLevels = [
  { name: 'Newbie', points: 0, icon: '🌱' },
  { name: 'Local Explorer', points: 500, icon: '🌳' },
  { name: 'Local Legend', points: 1000, icon: '🏆' },
];

export function PlacesPage({ onNavigate }: PlacesPageProps) {
  const [places, setPlaces] = useState<google.maps.MarkerOptions[]>([]);
  const [selectedPosition, setSelectedPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [transactionsAtLocation, setTransactionsAtLocation] = useState<Transaction[]>([]);
  const { isLoaded, error } = useGoogleMaps();

  // Calculate total local spending points (useMemo for efficiency)
  const localSpendingPoints = useMemo(() => {
    return mockTransactions
      .filter(tx => tx.location?.lat != null && tx.location?.lon != null && tx.amount < 0)
      .reduce((sum, tx) => sum + Math.abs(tx.amount), 0);
  }, []); // Empty dependency array means it calculates once

  // Determine current badge, next badge, and progress
  const { currentBadge, nextBadge, progressPercentage } = useMemo(() => {
    let currentBadge = { name: 'Newbie', points: 0, icon: '🌱' };
    let nextBadge = badgeLevels[0];
    let progressPercentage = 0;

    for (let i = 0; i < badgeLevels.length; i++) {
      if (localSpendingPoints >= badgeLevels[i].points) {
        currentBadge = badgeLevels[i];
        nextBadge = badgeLevels[i + 1]; // Might be undefined if it's the last badge
      } else {
        // If we haven't reached this level, this is the next goal
        nextBadge = badgeLevels[i];
        break;
      }
    }

    if (nextBadge) {
      const pointsInCurrentLevel = localSpendingPoints - currentBadge.points;
      const pointsForNextLevel = nextBadge.points - currentBadge.points;
      progressPercentage = Math.min(100, (pointsInCurrentLevel / pointsForNextLevel) * 100);
    } else {
      // Achieved the highest badge
      progressPercentage = 100;
    }

    return { currentBadge, nextBadge, progressPercentage };
  }, [localSpendingPoints]);

  // Load places from mock transactions when maps are ready
  useEffect(() => {
    if (isLoaded && google?.maps) {
      // SVG for a black pin with a white circle, anchored at the bottom center
      // Scaled up x2
      const svgPin = `
        <svg width="52" height="68" viewBox="-13 -30 26 34" xmlns="http://www.w3.org/2000/svg">
          <path d="M 0 0 q -11 -11 -11 -17 a 11 11 0 1 1 22 0 q 0 6 -11 17 z" fill="black"/>
          <circle cx="0" cy="-17" r="7" fill="white"/>
        </svg>
      `;
      const encodedSvg = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgPin)}`;

      const transactionPlaces = mockTransactions
        .filter(tx => tx.location?.lat != null && tx.location?.lon != null)
        .map(tx => ({
          position: { lat: tx.location!.lat!, lng: tx.location!.lon! },
          title: `${tx.merchant_name || tx.name} ($${Math.abs(tx.amount).toFixed(2)})`,
          icon: {
            url: encodedSvg,
            anchor: new google.maps.Point(26, 68),        // Bottom center of 52x68 SVG
            labelOrigin: new google.maps.Point(26, 26),   // Approx center of white circle for 52x68 SVG
          },
          label: {
            text: `$${Math.round(Math.abs(tx.amount))}`,
            color: 'black', // Black text inside the white circle
            fontSize: '12px', // Slightly larger font for bigger pin
            fontWeight: 'bold',
          } 
        }));
      setPlaces(transactionPlaces);
    }
  }, [isLoaded]); // Re-run when isLoaded changes

  // Handle marker click
  const handleMarkerClick = (marker: google.maps.Marker) => {
    const position = marker.getPosition();
    if (position) {
      const posLiteral = position.toJSON(); // { lat, lng }
      setSelectedPosition(posLiteral);
      console.log('Marker clicked at:', posLiteral.lat, posLiteral.lng);

      // Find transactions at the exact same location
      const nearby = mockTransactions.filter(tx => 
        tx.location?.lat === posLiteral.lat && tx.location?.lon === posLiteral.lng
      );
      setTransactionsAtLocation(nearby);
    }
  };

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Places</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>   
          </Breadcrumb>
        </div>
      </header>

      <main className="flex-1 overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Places</h1>
            <p className="text-muted-foreground">
              View and manage your favorite places
            </p>
          </div>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Add Place
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Map Container - Spans 2 columns on medium screens */}
          <div className="md:col-span-2">
            {error ? (
              <div className="h-[600px] flex items-center justify-center border rounded-lg">
                <p className="text-red-500">Error: {error}</p>
              </div>
            ) : isLoaded ? (
              <Map markers={places} onMarkerClick={handleMarkerClick} />
            ) : (
              <div className="h-[600px] flex items-center justify-center border rounded-lg">
                <p className="text-muted-foreground">Loading Google Maps...</p>
              </div>
            )}
          </div>

          {/* Right Column for Cards */}
          <div className="space-y-6">
            {/* Card for Nearby Transactions */}
            <Card>
              <CardHeader>
                <CardTitle>Transactions at Selected Location</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedPosition ? (
                  transactionsAtLocation.length > 0 ? (
                    <ul className="space-y-3">
                      {transactionsAtLocation.map((tx: Transaction) => (
                        <li key={tx.id} className="flex justify-between items-center border-b pb-2 gap-3">
                          {/* Logo and Transaction Info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0"> 
                            {tx.logo_url ? (
                              <img src={tx.logo_url} alt={tx.merchant_name || 'logo'} className="h-8 w-8 rounded-full object-contain flex-shrink-0" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground text-xs flex-shrink-0">
                                {/* Placeholder Icon or Initials */}
                                <span>{(tx.merchant_name || tx.name || '?').charAt(0)}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0"> {/* Allow text to truncate */}
                              <p className="font-medium truncate">{tx.merchant_name || tx.name}</p>
                              <p className="text-sm text-muted-foreground">{tx.date}</p>
                            </div>
                          </div>
                          {/* Amount */}
                          <p className={`font-semibold ${tx.amount > 0 ? 'text-green-600' : ''}`}>
                            ${Math.abs(tx.amount).toFixed(2)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground">No transactions found at this exact location.</p>
                  )
                ) : (
                  <p className="text-muted-foreground">Click a pin on the map to see transactions at that location.</p>
                )}
              </CardContent>
            </Card>

            {/* Local Spending Rewards Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                   Local Spending Rewards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Description */}
                <p className="text-sm text-muted-foreground">
                  Earn points for every dollar spent in your neighbourhood. Reach point milestones to unlock new badges!
                </p>

                {/* Badge and Points Display */}
                <div className="flex items-center justify-between pt-2"> {/* Added padding top */}
                  <span className="text-2xl mr-2">{currentBadge.icon}</span>
                  <div className="flex-1">
                     <p className="font-semibold">{currentBadge.name}</p>
                     <p className="text-sm text-muted-foreground">
                      {Math.floor(localSpendingPoints)} points
                     </p>
                  </div>
                </div>
  
                <Progress value={progressPercentage} className="h-2" />
  
                {nextBadge ? (
                   <p className="text-sm text-muted-foreground text-center">
                     {nextBadge.points - Math.floor(localSpendingPoints)} points to reach {nextBadge.name} ({nextBadge.icon})
                   </p>
                 ) : (
                   <p className="text-sm text-muted-foreground text-center">
                     You've reached the highest level! {currentBadge.icon}
                   </p>
                 )}

                {/* Placeholder Share Button */}
                <Button variant="outline" size="sm" className="w-full mt-4">
                  <Share2 className="mr-2 h-4 w-4" />
                  Share Progress
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
}